import { formatPoCurrency, formatPoLinePrice } from '../../services/purchaseOrderService';

// NULL means no price on record; 0 means free. Number() folds them together,
// so the read has to keep them apart before anything formats.
const priceOrNull = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};
import { buildPoLineDisplay } from '../../utils/poLinePack';
import { printReportDocument } from '../reports/ReportDriver';

const formatDateTime = (value) => {
  if (!value) return '—';
  try { return new Date(value).toLocaleString(); } catch { return String(value); }
};

const formatDeliveryDate = (value) => {
  if (!value) return 'Not specified';
  try {
    return new Date(`${value}T00:00:00`).toLocaleDateString(undefined, {
      year: 'numeric', month: 'long', day: 'numeric',
    });
  } catch { return String(value); }
};

export function printPurchaseOrder(po, storeName = '', storeAddress = '') {
  if (!po) return;
  const vendorName = po.vendor_name_snapshot || po.vendorName || 'Vendor';
  const contact = po.vendor_contact_snapshot || po.contact || {};
  const items = (po.items || []).map((item) => {
    const display = buildPoLineDisplay(item);
    const countDelivery = display.countedNeed
      ? [
        `Need ${display.countedNeed}`,
        display.delivers ? `delivers ${display.delivers}` : null,
        display.abovePar ? `${display.abovePar} above PAR` : null,
      ].filter(Boolean).join(' · ')
      : '—';
    return {
      description: item.description || 'Item',
      location: item.location || '-',
      packSize: display.pack,
      countDelivery,
      vendorOrder: display.qty,
      // NULL is "no price on record" and must stay distinguishable from a real
      // 0 all the way to the page — $0.00 on a printed order reads as free.
      unitPrice: priceOrNull(item.unit_price ?? item.unitPrice),
      lineTotal: priceOrNull(item.line_total ?? item.lineTotal),
    };
  });
  const unpricedCount = items.filter((it) => it.lineTotal === null).length;
  const total = items.reduce((sum, it) => sum + (it.lineTotal ?? 0), 0);
  const revisionNumber = po.revision_number || 0;
  const isAmended = revisionNumber > 0;
  const deliveryDate = po.requested_delivery_date || po.requestedDeliveryDate || null;
  const deliveryNotes = po.delivery_notes || po.deliveryNotes || null;
  const parentSentAt = po.revision_summary?.parentSentAt || null;
  const senderName = po.ordered_by_name || po.orderedBy?.name || '';
  const poNumber = po.po_number || po.poNumber || '';

  const subtitle = [
    poNumber,
    isAmended ? `Revision #${revisionNumber}` : null,
    po.is_latest_revision === false ? 'SUPERSEDED' : null,
  ].filter(Boolean).join(' — ');

  const extraMeta = [
    { label: 'Vendor', value: vendorName },
    { label: 'Contact', value: contact.contactName || '—' },
    { label: 'Email', value: contact.contactEmail || '—' },
    { label: 'Phone', value: contact.contactPhone || '—' },
    { label: 'Ordered By', value: senderName || '—' },
    { label: 'Status', value: po.delivery_status || po.deliveryStatus || '—' },
    { label: 'Delivery Date', value: formatDeliveryDate(deliveryDate) },
  ];
  if (deliveryNotes) extraMeta.push({ label: 'Delivery Notes', value: deliveryNotes });
  if (isAmended && parentSentAt) extraMeta.push({ label: 'Replaces Version Sent', value: formatDateTime(parentSentAt) });
  if (storeAddress) extraMeta.push({ label: 'Address', value: storeAddress });
  if (po.notes) extraMeta.push({ label: 'Notes', value: po.notes });

  const sentAt = po.sent_at || po.created_at;

  const alert = (() => {
    if (po.is_latest_revision === false) {
      return { text: 'SUPERSEDED — This purchase order has been replaced by a newer revision.', variant: 'danger' };
    }
    if (isAmended) {
      return { text: `AMENDED PURCHASE ORDER — Revision #${revisionNumber}. Please discard any previously sent copies.`, variant: 'warning' };
    }
    return null;
  })();

  const descriptor = {
    store: { name: storeName },
    report: {
      title: 'Purchase Order',
      subtitle,
      counters: [],
      createdBy: senderName,
      generatedAt: sentAt || new Date().toISOString(),
      meta: extraMeta,
      ...(alert ? { alert } : {}),
    },
    stats: [
      { label: 'Lines', value: String(items.length) },
      // A total that skips unpriced lines is a partial total, and says so.
      { label: 'Subtotal', value: formatPoCurrency(po.subtotal ?? total) },
      { label: 'Total', value: formatPoCurrency(po.total ?? total) },
      ...(unpricedCount > 0
        ? [{ label: 'Unpriced lines', value: String(unpricedCount) }]
        : []),
    ],
    sections: [
      {
        columns: [
          { key: 'num', label: '#', align: 'number', width: '32px' },
          { key: 'description', label: 'Item', align: 'text' },
          { key: 'location', label: 'Location', align: 'text', width: '90px' },
          // What the vendor ships, and what is inside it. "Order unit" alone
          // prices the line; the pack is what the receiver checks the pallet
          // and the invoice against.
          { key: 'packSize', label: 'Pack / Size', align: 'text', width: '120px' },
          { key: 'countDelivery', label: 'Count / delivery', align: 'text', width: '145px' },
          { key: 'vendorOrder', label: 'Vendor order', align: 'number', width: '80px' },
          { key: 'unitPrice', label: 'Unit Price', align: 'currency', width: '72px' },
          { key: 'lineTotal', label: 'Line Total', align: 'currency', width: '80px' },
        ],
        rows: items.map((it, idx) => ({
          num: String(idx + 1),
          description: it.description,
          location: it.location,
          packSize: it.packSize || '—',
          countDelivery: it.countDelivery,
          vendorOrder: it.vendorOrder || '—',
          unitPrice: formatPoLinePrice(it.unitPrice),
          lineTotal: formatPoLinePrice(it.lineTotal),
        })),
        totals: {
          description: 'Total',
          lineTotal: formatPoCurrency(po.total ?? total),
        },
      },
    ],
  };

  printReportDocument(descriptor);
}
